import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { Pause, Play, SkipBack, SkipForward } from "lucide-react";
import { CHAPTERS } from "./scenes";
import { ease } from "./kit";

const fmt = (ms) => { const s = Math.floor(ms / 1000); return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };
const TOTAL = CHAPTERS.reduce((a, c) => a + c.dur, 0);
export const FILM_LENGTH = fmt(TOTAL);

export default function Film() {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.25 });
  const reduce = useReducedMotion();
  const [s, setS] = useState({ ch: 0, t: 0 });
  const [paused, setPaused] = useState(false);
  const running = inView && !paused && !reduce;
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setS((x) => (x.t + 100 >= CHAPTERS[x.ch].dur ? { ch: (x.ch + 1) % CHAPTERS.length, t: 0 } : { ch: x.ch, t: x.t + 100 })), 100);
    return () => clearInterval(id);
  }, [running]);
  const { ch, t } = s;
  const c = CHAPTERS[ch];
  const phase = reduce ? c.beats.length : c.beats.filter((b) => b <= t).length;
  const go = (i) => setS({ ch: (i + CHAPTERS.length) % CHAPTERS.length, t: 0 });
  const elapsed = CHAPTERS.slice(0, ch).reduce((a, x) => a + x.dur, 0) + Math.min(t, c.dur);
  const btn = "inline-flex h-8 w-8 items-center justify-center border border-white/15 text-zinc-300 transition-colors hover:border-white/40 hover:text-white";
  return (
    <div ref={ref} className="grid grid-cols-1 border-t border-white/10 lg:grid-cols-12" data-testid="lifecycle">
      <aside className="order-2 min-w-0 lg:order-1 lg:col-span-4 lg:border-r lg:border-white/10">
        <div className="py-6 lg:sticky lg:top-14 lg:py-10 lg:pr-10">
          <ol className="hidden lg:block" data-testid="lifecycle-index">
            {CHAPTERS.map((s, i) => (
              <li key={s.key} className="border-b border-white/[.07] last:border-b-0">
                <button onClick={() => go(i)} data-testid={`lifecycle-step-${s.key}`} className={`flex w-full items-baseline gap-4 py-[9px] text-left transition-colors ${i === ch ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}>
                  <span className={`font-mono text-[10.5px] tabular-nums ${i === ch ? "text-amber-500" : ""}`}>{String(i + 1).padStart(2, "0")}</span>
                  <span className={`display text-[15px] ${i === ch ? "font-semibold" : "font-medium"}`}>{s.label}</span>
                  <span className="ml-auto font-mono text-[10px] tabular-nums text-zinc-600">{fmt(s.dur)}</span>
                </button>
                <AnimatePresence initial={false}>
                  {i === ch && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease }} className="overflow-hidden">
                      <div className="pb-5 pl-[38px]"><div className="display text-[19px] font-semibold leading-snug text-white">{s.title}</div><p className="mt-1.5 text-[13.5px] leading-relaxed text-zinc-400">{s.copy}</p></div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            ))}
          </ol>
          <div className="lg:hidden">
            <div className="mono-label amber">{String(ch + 1).padStart(2, "0")} — {c.label}</div>
            <div className="display mt-2 text-[20px] font-semibold leading-snug text-white">{c.title}</div>
            <p className="mt-2 text-[13.5px] leading-relaxed text-zinc-400">{c.copy}</p>
            <div className="snap-x -mx-5 mt-5 flex gap-1.5 overflow-x-auto px-5 pb-1" data-testid="lifecycle-mobile">
              {CHAPTERS.map((s, i) => <button key={s.key} onClick={() => go(i)} className={`shrink-0 border px-3 py-1.5 font-mono text-[10.5px] uppercase tracking-wider transition-colors ${i === ch ? "border-amber-500 text-amber-400" : "border-white/15 text-zinc-500"}`} data-testid={`lifecycle-chip-${s.key}`}>{String(i + 1).padStart(2, "0")} {s.label}</button>)}
            </div>
          </div>
        </div>
      </aside>
      <div className="order-1 min-w-0 lg:order-2 lg:col-span-8 lg:py-10 lg:pl-10">
        <div className="stage h-[560px] sm:h-auto sm:aspect-[4/3] xl:aspect-[3/2]" data-testid="film-stage">
          <div className="stage-grid" />
          <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between px-4 py-3 font-mono text-[10px] uppercase tracking-[.14em] text-zinc-500 sm:text-[10.5px]"><span className="truncate">TRAIDE · one job, start to finish</span><span className="flex shrink-0 items-center gap-2 tabular-nums"><span className={`h-1.5 w-1.5 rounded-full ${running ? "bg-rose-500 live-dot" : "bg-zinc-600"}`} />{fmt(elapsed)} / {fmt(TOTAL)}</span></div>
          <motion.div key={c.key} className="absolute inset-0 flex items-start justify-center px-3 pb-14 pt-12 sm:items-center sm:px-10 sm:pb-16 sm:pt-14" initial={{ opacity: 0, scale: 0.985 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease }} data-testid={`lifecycle-ui-${c.key}`}>
            <c.Scene phase={phase} />
          </motion.div>
          <div className="absolute inset-x-0 bottom-0 z-10 border-t border-white/10 bg-[#0E0E10]/92 px-3 py-2.5 backdrop-blur-sm sm:px-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <button onClick={() => go(ch - 1)} className={btn} aria-label="Previous chapter" data-testid="film-prev"><SkipBack size={13} /></button>
              <button onClick={() => setPaused((p) => !p)} className={btn} aria-label={paused ? "Play" : "Pause"} data-testid="film-toggle">{paused ? <Play size={13} /> : <Pause size={13} />}</button>
              <button onClick={() => go(ch + 1)} className={btn} aria-label="Next chapter" data-testid="film-next"><SkipForward size={13} /></button>
              <div className="flex flex-1 gap-[3px]">{CHAPTERS.map((s, i) => <button key={s.key} onClick={() => go(i)} className="relative h-[3px] flex-1 bg-white/10" title={s.label} aria-label={s.label}><span className="absolute inset-y-0 left-0 bg-amber-500 transition-[width] duration-100 ease-linear" style={{ width: i < ch ? "100%" : i === ch ? `${Math.min(100, (t / c.dur) * 100)}%` : 0 }} /></button>)}</div>
              <span className="hidden font-mono text-[10.5px] text-zinc-400 md:block">{String(ch + 1).padStart(2, "0")} / {CHAPTERS.length} · {c.label}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
